import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { useAuth } from "./auth/AuthProvider.jsx";
import { api } from "./api/client.js";
import ClientPicker from "./clients/ClientPicker.jsx";
import ClientsPage from "./clients/ClientsPage.jsx";
import EmployeesPage from "./employees/EmployeesPage.jsx";
import InternalPage from "./internal/InternalPage.jsx";
import ContactsPage from "./crm/ContactsPage.jsx";
import DashboardPage from "./dashboard/DashboardPage.jsx";
import TemplatesPage from "./templates/TemplatesPage.jsx";
import MeetingTemplatesPage from "./meetings/MeetingTemplatesPage.jsx";
import OnePagerBuilder from "./OnePagerBuilder.jsx";
// Lazy-loaded so pdf.js (~1.4 MB worker + lib) stays out of the main bundle and
// only loads when someone opens the converter.
const PdfToMarkdown = lazy(() => import("./PdfToMarkdown.jsx"));
import { LOGO_SVG, ICON_SVG } from "./assets/logos.js";
import {
  colors, font, NAV_ITEMS, MAIN_NAV, TOOL_NAV, navItem, accountNavFor, PROPOSAL_TYPES, BRANCHES,
  EVENT_TYPES, DISCUSSION_CATEGORIES, FRONT_DOOR_BRANCHES,
  generateId, formatCurrency, formatDate, getDefaultResponseDate,
  displayName, fullName, getEventType,
} from "./utils/theme.js";
import ProposalBoard from "./proposals/ProposalBoard.jsx";
import { TIERS } from "./clients/TierBadge.jsx";
import { isActiveStage, isPreSubmission } from "./proposals/stages.js";
import { dueUrgency } from "./proposals/urgency.js";
const monoFont = "'JetBrains Mono', monospace";

// ─── STYLES ───

// ─── SIDEBAR ───
// Self-contained spinner (SVG/SMIL, so no CSS keyframes needed for inline-styled pages).
function Spinner({ size = 18, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "block" }} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={color} strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke={color} strokeWidth="3" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

function Sidebar({ active, onNavigate, user, onLogout, notifications }) {
  return (
    <div style={{
      width: 240, minHeight: "100vh", background: colors.surface, borderRight: `1px solid ${colors.border}`,
      display: "flex", flexDirection: "column", padding: "24px 0", flexShrink: 0,
    }}>
      <div style={{ padding: "0 20px", marginBottom: 32, display: "flex", alignItems: "center", gap: 10 }}>
        <img src={ICON_SVG} alt="" style={{ height: 24 }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 3, color: colors.accent, textTransform: "uppercase" }}>Sawtooth</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.text, marginTop: -1 }}>Ops Hub</div>
        </div>
      </div>
      <nav>
        {MAIN_NAV.map((item) => {
          const isActive = active === item.id;
          const hasNotif = item.id === "proposals" && notifications > 0;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 20px",
                background: isActive ? colors.accentDim : "transparent", border: "none",
                borderRight: isActive ? `3px solid ${colors.accent}` : "3px solid transparent",
                color: isActive ? colors.text : colors.textMuted, cursor: "pointer", fontFamily: font,
                fontSize: 14, fontWeight: isActive ? 600 : 400, textAlign: "left", position: "relative",
                transition: "all 0.15s ease",
              }}
            >
              <span style={{ fontSize: 16, opacity: 0.7 }}>{item.icon}</span>
              {item.label}
              {hasNotif && (
                <span style={{
                  position: "absolute", right: 16, background: colors.danger, color: "#fff",
                  fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                }}>{notifications}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div style={{ margin: "16px 20px 0", borderTop: `1px solid ${colors.border}`, paddingTop: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: colors.textDim, textTransform: "uppercase", marginBottom: 4 }}>Account Owners</div>
        {accountNavFor(user).map((item) => {
          const isActive = active === item.id;
          const soon = !!item.comingSoon;
          return (
            <button
              key={item.id}
              onClick={soon ? undefined : () => onNavigate(item.id)}
              disabled={soon}
              title={soon ? "Coming soon" : undefined}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 0", width: "100%",
                background: "transparent", border: "none", textAlign: "left", fontFamily: font, fontSize: 14,
                color: isActive ? colors.text : colors.textMuted, fontWeight: isActive ? 600 : 400,
                cursor: soon ? "default" : "pointer", opacity: soon ? 0.55 : 1,
              }}
            >
              <span style={{ fontSize: 13, opacity: 0.7 }}>{item.icon}</span>
              {item.label}
              {soon && (
                <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: colors.textDim, border: `1px solid ${colors.border}`, borderRadius: 4, padding: "1px 5px" }}>Soon</span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ margin: "16px 20px 0", borderTop: `1px solid ${colors.border}`, paddingTop: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: colors.textDim, textTransform: "uppercase", marginBottom: 4 }}>Tools</div>
        <a
          href="https://proposals.sawtoothadvisors.io"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
            color: colors.textMuted, textDecoration: "none", fontSize: 14, fontFamily: font,
          }}
        >
          <span style={{ fontSize: 13, opacity: 0.7 }}>↗</span>
          Proposal Writer
        </a>
        {TOOL_NAV.map((item) => (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
              color: active === item.id ? colors.text : colors.textMuted,
              background: "transparent", border: "none", cursor: "pointer",
              fontSize: 14, fontFamily: font, width: "100%", textAlign: "left",
            }}
          >
            <span style={{ fontSize: 13, opacity: 0.7 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ padding: "16px 20px", borderTop: `1px solid ${colors.border}` }}>
        <button onClick={() => onNavigate("settings")} style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 10, width: "100%",
          background: "transparent", border: "none", cursor: "pointer", padding: 0, textAlign: "left",
        }}>
          {user.photo ? (
            <img src={user.photo} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 32, height: 32, borderRadius: "50%", background: colors.accentDim,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600,
              color: colors.accent, flexShrink: 0,
            }}>{(user.firstName || user.name || "?")[0].toUpperCase()}</div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: colors.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName(user)}</div>
            <div style={{ fontSize: 11, color: colors.accent, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>{user.role}</div>
          </div>
        </button>
        <button onClick={onLogout} style={{
          background: "transparent", border: `1px solid ${colors.borderLight}`, color: colors.textMuted,
          padding: "6px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer", fontFamily: font, width: "100%",
        }}>Sign Out</button>
      </div>
    </div>
  );
}

// ─── HOME SCREEN ───
// Placeholder for main-menu pages that exist in the nav but land in a later v2 increment.
function ComingSoon({ title }) {
  return (
    <div style={{ padding: 60, textAlign: "center", color: colors.textDim }}>
      <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>◵</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 14 }}>Coming soon — this page is being built in the v2 rollout.</div>
    </div>
  );
}

function HomeScreen({ onNavigate, proposals, archived, events, discussions }) {
  const submitted = proposals.filter((p) => p.stage === "submitted");
  const pipelineValue = submitted.reduce((s, p) => s + (Number(p.value) || 0), 0);

  // Per-card live stats, keyed by nav id. Cards without an entry render no stat line.
  const blockers = discussions?.filter((d) => d.category === "blocker").length || 0;
  const statsFor = {
    proposals: {
      stats: `${submitted.length} awaiting · ${formatCurrency(pipelineValue)} pipeline`,
    },
    calendar: { stats: `${events?.length || 0} events scheduled` },
    discussion: {
      stats: discussions?.length > 0 ? `${discussions.length} item${discussions.length !== 1 ? "s" : ""} for next meeting` : "No items queued",
      alert: blockers > 0 ? `${blockers} blocker${blockers !== 1 ? "s" : ""}` : null,
    },
  };
  // Cards are derived from the nav definition (single source of truth) — never a
  // second hardcoded list. Every main-menu record except Home gets a card.
  const cards = MAIN_NAV.filter((n) => n.id !== "home").map((n) => ({
    id: n.id, icon: n.icon, title: n.label, desc: n.desc,
    stats: statsFor[n.id]?.stats || "", alert: statsFor[n.id]?.alert || null,
  }));

  return (
    <div>
      <div style={{ marginBottom: 36 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: colors.text, letterSpacing: -0.5 }}>Welcome back</h1>
        <p style={{ margin: "8px 0 0", color: colors.textDim, fontSize: 15 }}>Sawtooth Advisors Operations Hub</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        {cards.map((card) => {
          const isActive = true; // every main-menu page is navigable
          return (
            <button
              key={card.id}
              onClick={() => onNavigate(card.id)}
              style={{
                background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 14,
                padding: 28, textAlign: "left", cursor: isActive ? "pointer" : "default",
                opacity: isActive ? 1 : 0.5, fontFamily: font,
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 12, color: colors.accent }}>{card.icon}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 6 }}>{card.title}</div>
              <div style={{ fontSize: 13, color: colors.textDim, marginBottom: 14, lineHeight: 1.5 }}>{card.desc}</div>
              <div style={{ fontSize: 12, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>{card.stats}</div>
              {card.alert && (
                <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: colors.danger, padding: "4px 10px", background: "rgba(239,68,68,0.1)", borderRadius: 5, display: "inline-block" }}>
                  {card.alert}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── CALENDAR ───



function EventModal({ event, users, onSave, onClose, onDelete, modalRaw }) {
  const [form, setForm] = useState(() => {
    if (event) return event;
    const prefillDate = typeof modalRaw === "string" && modalRaw.startsWith("new-") ? modalRaw.slice(4) : "";
    return {
      id: generateId(), title: "", type: "bd-conference", date: prefillDate, endDate: "",
      location: "", description: "", createdBy: "", allDay: true,
    };
  });

  const update = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const inputStyle = {
    width: "100%", padding: "10px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 6, color: colors.text, fontSize: 14, fontFamily: font, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 6 };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        width: 520, maxHeight: "85vh", overflow: "auto", background: colors.surface,
        border: `1px solid ${colors.border}`, borderRadius: 16, padding: 32,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.text }}>{event ? "Edit Event" : "New Event"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: colors.textDim, fontSize: 24, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Title *</label>
          <input style={inputStyle} value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Event name" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Type</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {EVENT_TYPES.map((t) => (
              <button key={t.id} onClick={() => update("type", t.id)} style={{
                padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font,
                background: form.type === t.id ? t.bg : "transparent",
                border: `1px solid ${form.type === t.id ? t.border : colors.borderLight}`,
                color: form.type === t.id ? t.color : colors.textMuted,
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Start Date *</label>
            <input style={inputStyle} type="date" value={form.date} onChange={(e) => update("date", e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>End Date</label>
            <input style={inputStyle} type="date" value={form.endDate} onChange={(e) => update("endDate", e.target.value)} />
            <div style={{ fontSize: 10, color: colors.textDim, marginTop: 4 }}>Leave blank for single-day</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Location</label>
          <input style={inputStyle} value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="City, venue, or virtual" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Description</label>
          <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Details, links, notes..." />
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 8 }}>
          <div>
            {event && onDelete && (
              <button onClick={() => onDelete(event.id)} style={{
                padding: "10px 16px", background: "transparent", border: `1px solid rgba(239,68,68,0.3)`,
                borderRadius: 8, color: colors.danger, fontSize: 13, cursor: "pointer", fontFamily: font,
              }}>Delete</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={onClose} style={{
              padding: "10px 20px", background: "transparent", border: `1px solid ${colors.borderLight}`,
              borderRadius: 8, color: colors.textMuted, fontSize: 14, cursor: "pointer", fontFamily: font,
            }}>Cancel</button>
            <button
              onClick={() => { if (form.title && form.date) onSave(form); }}
              style={{
                padding: "10px 24px", background: colors.accent, border: "none", borderRadius: 8,
                color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: font,
                opacity: (form.title && form.date) ? 1 : 0.4,
              }}
            >{event ? "Update" : "Add Event"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyCalendar({ user, users }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    api.events.list().then((d) => { setEvents(d || []); setLoading(false); });
  }, []);

  const handleSave = async (evt) => {
    const saved = { ...evt, updatedAt: new Date().toISOString() };
    if (!events.find((e) => e.id === evt.id)) {
      saved.createdBy = user.email;
      saved.createdAt = new Date().toISOString();
    }
    await api.events.save(saved);
    const exists = events.find((e) => e.id === evt.id);
    setEvents(exists ? events.map((e) => (e.id === evt.id ? saved : e)) : [...events, saved]);
    setModal(null);
  };

  const handleDelete = async (id) => {
    await api.events.delete(id);
    setEvents(events.filter((e) => e.id !== id));
    setModal(null);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const filteredEvents = filterType === "all" ? events : events.filter((e) => e.type === filterType);

  const toDateStr = (d) => {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  const todayStr = toDateStr(new Date());
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Build weeks for month view
  const buildWeeks = () => {
    const first = new Date(year, month, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const allDays = [];
    for (let i = startDay - 1; i >= 0; i--) allDays.push({ date: new Date(year, month - 1, prevMonthDays - i), current: false });
    for (let i = 1; i <= daysInMonth; i++) allDays.push({ date: new Date(year, month, i), current: true });
    const remaining = 42 - allDays.length;
    for (let i = 1; i <= remaining; i++) allDays.push({ date: new Date(year, month + 1, i), current: false });
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) weeks.push(allDays.slice(i, i + 7));
    return weeks;
  };

  // Build single week
  const buildCurrentWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - d.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) { days.push({ date: new Date(d), current: true }); d.setDate(d.getDate() + 1); }
    return [days];
  };

  // Assign event lanes within a week to avoid overlap
  const getWeekEvents = (weekDays) => {
    const weekStart = toDateStr(weekDays[0].date);
    const weekEnd = toDateStr(weekDays[6].date);
    const relevant = filteredEvents.filter((e) => {
      const eStart = e.date;
      const eEnd = e.endDate || e.date;
      return eStart <= weekEnd && eEnd >= weekStart;
    }).sort((a, b) => {
      const diff = a.date.localeCompare(b.date);
      if (diff !== 0) return diff;
      return (b.endDate || b.date).localeCompare(a.endDate || a.date);
    });

    const lanes = [];
    const placed = [];

    relevant.forEach((evt) => {
      const eStart = evt.date < weekStart ? weekStart : evt.date;
      const eEnd = (evt.endDate || evt.date) > weekEnd ? weekEnd : (evt.endDate || evt.date);
      const startCol = weekDays.findIndex((d) => toDateStr(d.date) === eStart);
      const endCol = weekDays.findIndex((d) => toDateStr(d.date) === eEnd);
      if (startCol === -1 || endCol === -1) return;

      let lane = 0;
      while (true) {
        const conflict = placed.some((p) => p.lane === lane && !(endCol < p.startCol || startCol > p.endCol));
        if (!conflict) break;
        lane++;
      }
      placed.push({ ...evt, startCol, endCol, lane });
      if (lane >= lanes.length) lanes.push([]);
      lanes[lane] = lanes[lane] || [];
    });

    return { placed, maxLane: placed.length > 0 ? Math.max(...placed.map((p) => p.lane)) + 1 : 0 };
  };

  const headerText = () => {
    if (view === "month") return `${monthNames[month]} ${year}`;
    const wk = buildCurrentWeek()[0];
    const s = wk[0].date, e = wk[6].date;
    if (s.getMonth() === e.getMonth()) return `${monthNames[s.getMonth()]} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
    return `${monthNames[s.getMonth()]} ${s.getDate()} – ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
  };

  // Upcoming events
  const upcoming = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const limit = new Date(today); limit.setDate(limit.getDate() + 30);
    return filteredEvents
      .filter((e) => new Date(e.date) >= today && new Date(e.date) <= limit)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredEvents]);

  const renderWeekRow = (weekDays, weekIdx, isWeekView) => {
    const { placed, maxLane } = getWeekEvents(weekDays);
    const eventAreaHeight = Math.max(maxLane * 24, 0);
    const cellHeight = isWeekView ? Math.max(200, eventAreaHeight + 60) : Math.max(90, eventAreaHeight + 32);

    return (
      <div key={weekIdx} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", position: "relative" }}>
        {weekDays.map((day, colIdx) => {
          const ds = toDateStr(day.date);
          const isToday = ds === todayStr;
          return (
            <div
              key={colIdx}
              onClick={() => setModal("new-" + ds)}
              style={{
                minHeight: cellHeight, padding: isWeekView ? "10px 8px" : "6px 6px",
                borderBottom: `1px solid ${colors.border}`,
                borderRight: colIdx < 6 ? `1px solid ${colors.border}` : "none",
                background: isToday ? "rgba(59,130,246,0.06)" : "transparent",
                cursor: "pointer", opacity: day.current ? 1 : 0.35,
                position: "relative",
              }}
            >
              <div style={{
                fontSize: isWeekView ? 13 : 12, fontWeight: isToday ? 700 : 400,
                color: isToday ? colors.accent : colors.text,
                width: isWeekView ? 30 : 24, height: isWeekView ? 30 : 24,
                lineHeight: isWeekView ? "30px" : "24px", textAlign: "center", borderRadius: "50%",
                background: isToday ? colors.accentDim : "transparent",
                marginBottom: 2,
              }}>{day.date.getDate()}</div>
            </div>
          );
        })}
        {/* Event bars overlaid */}
        {placed.map((evt) => {
          const t = getEventType(evt.type);
          const leftPct = (evt.startCol / 7) * 100;
          const widthPct = ((evt.endCol - evt.startCol + 1) / 7) * 100;
          const topOffset = (isWeekView ? 44 : 30) + evt.lane * 24;
          const isStartInWeek = evt.date >= toDateStr(weekDays[0].date);
          const isEndInWeek = (evt.endDate || evt.date) <= toDateStr(weekDays[6].date);
          return (
            <button
              key={evt.id + "-" + weekIdx}
              onClick={(e) => { e.stopPropagation(); setModal(evt); }}
              style={{
                position: "absolute", top: topOffset,
                left: `calc(${leftPct}% + 3px)`,
                width: `calc(${widthPct}% - 6px)`,
                height: 20, padding: "0 8px",
                background: t.bg, border: `1px solid ${t.border}`,
                borderRadius: isStartInWeek && isEndInWeek ? 5 : isStartInWeek ? "5px 0 0 5px" : isEndInWeek ? "0 5px 5px 0" : 0,
                borderRight: isEndInWeek ? `1px solid ${t.border}` : "none",
                borderLeft: isStartInWeek ? `1px solid ${t.border}` : "none",
                color: t.color, fontSize: 11, fontWeight: 600, cursor: "pointer",
                fontFamily: font, textAlign: "left", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "20px",
                display: "flex", alignItems: "center", zIndex: 2,
              }}
            >{evt.title}</button>
          );
        })}
      </div>
    );
  };

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading calendar...</div>;

  const weeks = view === "month" ? buildWeeks() : buildCurrentWeek();

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", background: colors.bg, borderRadius: 8, border: `1px solid ${colors.borderLight}`, overflow: "hidden" }}>
          {["month", "week"].map((v) => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "8px 16px", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer",
              fontFamily: font, textTransform: "capitalize",
              background: view === v ? colors.accent : "transparent",
              color: view === v ? "#fff" : colors.textMuted,
            }}>{v}</button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => navigate(-1)} style={{
            background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 6,
            color: colors.textMuted, padding: "6px 10px", cursor: "pointer", fontSize: 14, fontFamily: font,
          }}>‹</button>
          <button onClick={goToday} style={{
            background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 6,
            color: colors.text, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: font,
          }}>Today</button>
          <button onClick={() => navigate(1)} style={{
            background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 6,
            color: colors.textMuted, padding: "6px 10px", cursor: "pointer", fontSize: 14, fontFamily: font,
          }}>›</button>
          <span style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginLeft: 8 }}>{headerText()}</span>
        </div>

        <div style={{ flex: 1 }} />

        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{
          padding: "8px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
          borderRadius: 8, color: colors.text, fontSize: 12, fontFamily: font,
        }}>
          <option value="all">All Types</option>
          {EVENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>

        <button onClick={() => setModal("new")} style={{
          padding: "8px 20px", background: colors.accent, border: "none", borderRadius: 8,
          color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font,
        }}>+ New Event</button>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        {EVENT_TYPES.map((t) => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: t.color }} />
            <span style={{ fontSize: 11, color: colors.textMuted }}>{t.label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 24 }}>
        {/* Calendar grid */}
        <div style={{ flex: 1 }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
            {/* Day headers */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {dayNames.map((d) => (
                <div key={d} style={{
                  padding: "10px 8px", textAlign: "center", fontSize: 11, fontWeight: 600,
                  color: colors.textDim, letterSpacing: 1, textTransform: "uppercase",
                  background: colors.surfaceAlt, borderBottom: `1px solid ${colors.border}`,
                }}>{d}</div>
              ))}
            </div>
            {/* Week rows */}
            {weeks.map((week, i) => renderWeekRow(week, i, view === "week"))}
          </div>
        </div>

        {/* Upcoming Sidebar */}
        <div style={{ width: 260, flexShrink: 0 }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase" }}>
              Upcoming (30 days)
            </h3>
            {upcoming.length === 0 ? (
              <div style={{ fontSize: 12, color: colors.textDim, padding: "12px 0" }}>No upcoming events</div>
            ) : upcoming.slice(0, 10).map((evt) => {
              const t = getEventType(evt.type);
              return (
                <button
                  key={evt.id}
                  onClick={() => setModal(evt)}
                  style={{
                    display: "block", width: "100%", padding: "10px 0",
                    background: "transparent", border: "none", borderBottom: `1px solid ${colors.border}`,
                    cursor: "pointer", fontFamily: font, textAlign: "left",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: t.color, flexShrink: 0 }} />
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{evt.title}</div>
                  </div>
                  <div style={{ fontSize: 11, color: colors.textDim, marginTop: 3, marginLeft: 16 }}>
                    {formatDate(evt.date)}{evt.endDate && evt.endDate !== evt.date ? ` – ${formatDate(evt.endDate)}` : ""}
                    {evt.location ? ` · ${evt.location}` : ""}
                  </div>
                </button>
              );
            })}
            {upcoming.length > 10 && (
              <div style={{ fontSize: 11, color: colors.textDim, marginTop: 8 }}>+{upcoming.length - 10} more events</div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <EventModal
          event={typeof modal === "string" ? null : modal}
          modalRaw={modal}
          users={users}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}


// ─── DISCUSSION ITEMS ───

function DiscussionItems({ user, users }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [sortBy, setSortBy] = useState("votes");
  const [resolvingId, setResolvingId] = useState(null);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formCat, setFormCat] = useState("decision");
  const [formProject, setFormProject] = useState("");

  useEffect(() => {
    api.discussions.list().then((d) => { setItems(d || []); setLoading(false); });
  }, []);

  const handleSubmit = async () => {
    if (!formTitle.trim()) return;
    const newItem = {
      id: `disc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: formTitle.trim(),
      description: formDesc.trim(),
      category: formCat,
      submittedBy: user?.name || "Unknown",
      createdAt: new Date().toISOString(),
      upvotes: 0,
      upvotedBy: [],
      linkedProject: formProject || null,
    };
    await api.discussions.save(newItem);
    setItems([newItem, ...items]);
    setFormTitle(""); setFormDesc(""); setFormCat("decision"); setFormProject("");
    setShowForm(false);
  };

  const handleUpvote = async (id) => {
    const userName = user?.name || "Unknown";
    const item = items.find((i) => i.id === id);
    if (!item) return;
    const already = (item.upvotedBy || []).includes(userName);
    const updated = {
      ...item,
      upvotedBy: already ? item.upvotedBy.filter((n) => n !== userName) : [...(item.upvotedBy || []), userName],
      upvotes: already ? (item.upvotes || 1) - 1 : (item.upvotes || 0) + 1,
    };
    await api.discussions.save(updated);
    setItems(items.map((i) => (i.id === id ? updated : i)));
  };

  const handleResolve = (id) => {
    setResolvingId(id);
    setTimeout(async () => {
      await api.discussions.delete(id);
      setItems(items.filter((i) => i.id !== id));
      setResolvingId(null);
    }, 600);
  };

  const getCat = (id) => DISCUSSION_CATEGORIES.find((c) => c.id === id) || DISCUSSION_CATEGORIES[4];

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  const filtered = useMemo(() => {
    let list = filterCat === "all" ? [...items] : items.filter((i) => i.category === filterCat);
    if (sortBy === "votes") list.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0) || new Date(b.createdAt) - new Date(a.createdAt));
    else list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return list;
  }, [items, filterCat, sortBy]);

  const catCounts = useMemo(() => {
    const counts = { all: items.length };
    items.forEach((i) => { counts[i.category] = (counts[i.category] || 0) + 1; });
    return counts;
  }, [items]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: colors.textDim }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: "9px 20px", background: showForm ? "transparent" : colors.accent,
          border: showForm ? `1px solid ${colors.borderLight}` : "none", borderRadius: 8,
          color: showForm ? colors.textMuted : "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font,
        }}>{showForm ? "Cancel" : "+ New Item"}</button>
        <div style={{ flex: 1 }} />
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{
          padding: "9px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
          borderRadius: 8, color: colors.text, fontSize: 12, fontFamily: font,
        }}>
          <option value="votes">Sort by Votes</option>
          <option value="recent">Sort by Recent</option>
        </select>
      </div>

      {/* Category Filter Pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={() => setFilterCat("all")} style={{
          padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font,
          background: filterCat === "all" ? colors.accentDim : colors.bg,
          border: `1px solid ${filterCat === "all" ? colors.accent : colors.borderLight}`,
          color: filterCat === "all" ? colors.accent : colors.textMuted,
        }}>All ({catCounts.all || 0})</button>
        {DISCUSSION_CATEGORIES.map((cat) => (
          <button key={cat.id} onClick={() => setFilterCat(cat.id)} style={{
            padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font,
            background: filterCat === cat.id ? cat.bg : colors.bg,
            border: `1px solid ${filterCat === cat.id ? cat.color : colors.borderLight}`,
            color: filterCat === cat.id ? cat.color : colors.textMuted,
          }}>{cat.label} {catCounts[cat.id] ? `(${catCounts[cat.id]})` : ""}</button>
        ))}
      </div>

      {/* New Item Form */}
      {showForm && (
        <div style={{
          background: colors.surface, border: `1px solid ${colors.accent}40`, borderRadius: 12,
          padding: 24, marginBottom: 20, boxShadow: `0 0 24px ${colors.accent}10`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>
            New Discussion Item
          </div>

          <input value={formTitle} onChange={(e) => setFormTitle(e.target.value)}
            placeholder="What needs to be discussed?" maxLength={120}
            style={{
              width: "100%", padding: "12px 14px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
              borderRadius: 8, color: colors.text, fontSize: 15, fontWeight: 600, fontFamily: font,
              outline: "none", marginBottom: 12, boxSizing: "border-box",
            }} />

          <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)}
            placeholder="Add context, links, or details (optional)" rows={3}
            style={{
              width: "100%", padding: "10px 14px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
              borderRadius: 8, color: colors.text, fontSize: 13, fontFamily: font, resize: "vertical",
              outline: "none", marginBottom: 12, boxSizing: "border-box",
            }} />

          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            {/* Category Selector */}
            <div style={{ display: "flex", gap: 6 }}>
              {DISCUSSION_CATEGORIES.map((cat) => (
                <button key={cat.id} onClick={() => setFormCat(cat.id)} style={{
                  padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: font,
                  background: formCat === cat.id ? cat.bg : "transparent",
                  border: `1px solid ${formCat === cat.id ? cat.color : colors.borderLight}`,
                  color: formCat === cat.id ? cat.color : colors.textDim,
                }}>{cat.label}</button>
              ))}
            </div>

            <div style={{ flex: 1 }} />

            {/* Project Link (optional) */}
            <input value={formProject} onChange={(e) => setFormProject(e.target.value)}
              placeholder="Link to project (optional)" maxLength={80}
              style={{
                padding: "8px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
                borderRadius: 8, color: colors.text, fontSize: 12, fontFamily: font, outline: "none", width: 200,
              }} />

            <button onClick={handleSubmit} disabled={!formTitle.trim()} style={{
              padding: "10px 24px", background: formTitle.trim() ? colors.accent : colors.bg,
              border: "none", borderRadius: 8, color: formTitle.trim() ? "#fff" : colors.textDim,
              fontSize: 13, fontWeight: 600, cursor: formTitle.trim() ? "pointer" : "default", fontFamily: font,
            }}>Submit</button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && !showForm && (
        <div style={{
          background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12,
          padding: 60, textAlign: "center",
        }}>
          <div style={{ fontSize: 48, opacity: 0.3, marginBottom: 16 }}>◧</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 8 }}>No discussion items</div>
          <div style={{ fontSize: 13, color: colors.textDim, maxWidth: 360, margin: "0 auto 20px", lineHeight: 1.6 }}>
            Add items your team needs to discuss in the next meeting. Upvote to prioritize, resolve when done.
          </div>
          <button onClick={() => setShowForm(true)} style={{
            padding: "10px 24px", background: colors.accent, border: "none", borderRadius: 8,
            color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font,
          }}>Add First Item</button>
        </div>
      )}

      {/* Items List */}
      {filtered.length === 0 && items.length > 0 && (
        <div style={{ textAlign: "center", padding: 40, color: colors.textDim, fontSize: 13 }}>
          No items in this category.
        </div>
      )}

      {filtered.map((item) => {
        const cat = getCat(item.category);
        const isResolving = resolvingId === item.id;
        const hasVoted = (item.upvotedBy || []).includes(user?.name || "");
        return (
          <div key={item.id} style={{
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12,
            marginBottom: 10, overflow: "hidden",
            opacity: isResolving ? 0 : 1, transform: isResolving ? "translateX(60px) scale(0.95)" : "none",
            transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            maxHeight: isResolving ? 0 : 500, padding: isResolving ? "0 20px" : undefined,
          }}>
            <div style={{ display: "flex", gap: 0 }}>
              {/* Upvote Column */}
              <button onClick={() => handleUpvote(item.id)} style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                padding: "16px 16px", background: hasVoted ? colors.accentDim : "transparent",
                border: "none", borderRight: `1px solid ${colors.border}`, cursor: "pointer",
                minWidth: 56, transition: "background 0.15s",
              }}>
                <span style={{
                  fontSize: 16, color: hasVoted ? colors.accent : colors.textDim,
                  transform: hasVoted ? "scale(1.2)" : "scale(1)", transition: "all 0.2s",
                }}>▲</span>
                <span style={{
                  fontSize: 16, fontWeight: 700, color: hasVoted ? colors.accent : colors.text,
                  fontFamily: font, fontFeatureSettings: '"tnum"', marginTop: 2,
                }}>{item.upvotes || 0}</span>
              </button>

              {/* Content */}
              <div style={{ flex: 1, padding: "16px 20px" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  {/* Category Badge */}
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
                    color: cat.color, background: cat.bg, letterSpacing: 0.5, textTransform: "uppercase",
                    whiteSpace: "nowrap", flexShrink: 0, marginTop: 2,
                  }}>{cat.label}</span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: colors.text, lineHeight: 1.4 }}>
                      {item.title}
                    </div>
                  </div>
                </div>

                {item.description && (
                  <div style={{
                    fontSize: 13, color: colors.textMuted, lineHeight: 1.6, marginBottom: 10,
                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                  }}>{item.description}</div>
                )}

                {item.linkedProject && (
                  <div style={{ marginBottom: 10 }}>
                    <span style={{
                      fontSize: 11, color: colors.accent, background: colors.accentDim,
                      padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                    }}>▣ {item.linkedProject}</span>
                  </div>
                )}

                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: colors.textDim }}>
                    {item.submittedBy}
                  </span>
                  <span style={{ fontSize: 11, color: colors.textDim, opacity: 0.5 }}>·</span>
                  <span style={{ fontSize: 11, color: colors.textDim }}>{timeAgo(item.createdAt)}</span>

                  {(item.upvotedBy || []).length > 0 && (
                    <>
                      <span style={{ fontSize: 11, color: colors.textDim, opacity: 0.5 }}>·</span>
                      <span style={{ fontSize: 11, color: colors.textDim }}>
                        {item.upvotedBy.length === 1 ? item.upvotedBy[0] : `${item.upvotedBy[0]} +${item.upvotedBy.length - 1}`} voted
                      </span>
                    </>
                  )}

                  <div style={{ flex: 1 }} />

                  <button onClick={() => handleResolve(item.id)} style={{
                    padding: "5px 14px", background: "transparent", border: `1px solid rgba(16,185,129,0.3)`,
                    borderRadius: 6, color: colors.success, fontSize: 11, fontWeight: 600,
                    cursor: "pointer", fontFamily: font, transition: "all 0.15s",
                  }}
                    onMouseEnter={(e) => { e.target.style.background = "rgba(16,185,129,0.1)"; }}
                    onMouseLeave={(e) => { e.target.style.background = "transparent"; }}
                  >✓ Resolve</button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Meeting Summary Footer */}
      {items.length > 0 && (
        <div style={{
          marginTop: 24, padding: "16px 20px", background: colors.surfaceAlt,
          border: `1px solid ${colors.border}`, borderRadius: 10,
          display: "flex", gap: 24, alignItems: "center", justifyContent: "center", flexWrap: "wrap",
        }}>
          {DISCUSSION_CATEGORIES.map((cat) => {
            const count = catCounts[cat.id] || 0;
            return count > 0 ? (
              <span key={cat.id} style={{ fontSize: 12, color: cat.color, fontWeight: 600 }}>
                {count} {cat.label}{count !== 1 ? "s" : ""}
              </span>
            ) : null;
          })}
          <span style={{ fontSize: 12, color: colors.textDim }}>
            {items.length} item{items.length !== 1 ? "s" : ""} for next meeting
          </span>
        </div>
      )}
    </div>
  );
}

function CopyEmail({ email }) {
  const [copied, setCopied] = useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: colors.textMuted }}>{email}</span>
      <button
        onClick={handleCopy}
        title="Copy email"
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          padding: "1px 4px", borderRadius: 3, fontSize: 10, fontFamily: font,
          color: copied ? colors.success : colors.textDim,
          flexShrink: 0,
        }}
      >{copied ? "✓" : "Copy"}</button>
    </span>
  );
}

// ─── FRONT DOOR PROGRAMS ───

function emptyForm(type) {
  if (type === "org") return { program: "", branch: "", link: "" };
  return { program: "", branch: "", location: "", email: "", phone: "", link: "" };
}

function ProgramTable({ title, type, programs, user, onSave, onDelete }) {
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(() => emptyForm(type));
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const filtered = useMemo(() => {
    return programs.filter((p) => {
      const matchSearch = search === "" || (p.program || "").toLowerCase().includes(search.toLowerCase());
      const matchBranch = branchFilter === "all" || p.branch === branchFilter;
      return matchSearch && matchBranch;
    });
  }, [programs, search, branchFilter]);

  const handleAdd = async () => {
    if (!addForm.program.trim() || !addForm.branch) return;
    setSaving(true);
    try {
      const newItem = {
        id: generateId(),
        type,
        ...addForm,
        program: addForm.program.trim(),
        createdAt: new Date().toISOString(),
        createdBy: user.name || user.email || "Unknown",
      };
      await onSave(newItem);
      setAddForm(emptyForm(type));
      setShowAddForm(false);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ ...p });
    setShowAddForm(false);
    setExpandedIds((prev) => { const next = new Set(prev); next.delete(p.id); return next; });
  };

  const handleEditSave = async () => {
    if (!editForm.program.trim() || !editForm.branch) return;
    setSaving(true);
    try {
      await onSave({ ...editForm, program: editForm.program.trim() });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isBase = type === "base";

  const inputStyle = {
    padding: "5px 8px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 5, color: colors.text, fontSize: 12, fontFamily: font, outline: "none", width: "100%",
  };

  const selectStyle = { ...inputStyle };

  const thStyle = {
    padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600,
    color: colors.textDim, letterSpacing: 1, textTransform: "uppercase",
    borderBottom: `1px solid ${colors.border}`, background: colors.surfaceAlt,
  };

  const tdStyle = {
    padding: "11px 14px", borderBottom: `1px solid ${colors.border}`,
    color: colors.text, fontSize: 13, verticalAlign: "middle",
  };

  const renderAddForm = () => (
    <tr>
      <td style={tdStyle}>
        <input style={inputStyle} placeholder="Program name" value={addForm.program}
          onChange={(e) => setAddForm((f) => ({ ...f, program: e.target.value }))} />
      </td>
      <td style={tdStyle}>
        <select style={selectStyle} value={addForm.branch}
          onChange={(e) => setAddForm((f) => ({ ...f, branch: e.target.value }))}>
          <option value="">Branch...</option>
          {FRONT_DOOR_BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </td>
      {isBase && (
        <>
          <td style={tdStyle}>
            <input style={inputStyle} placeholder="Location" value={addForm.location}
              onChange={(e) => setAddForm((f) => ({ ...f, location: e.target.value }))} />
          </td>
          <td style={tdStyle}>
            <input style={inputStyle} placeholder="Email" value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} />
          </td>
          <td style={tdStyle}>
            <input style={inputStyle} placeholder="Phone" value={addForm.phone}
              onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} />
          </td>
        </>
      )}
      <td style={tdStyle}>
        <input style={inputStyle} placeholder="https://..." value={addForm.link}
          onChange={(e) => setAddForm((f) => ({ ...f, link: e.target.value }))} />
      </td>
      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        <button onClick={handleAdd} disabled={saving} style={{
          padding: "4px 12px", background: colors.accent, color: "#fff", border: "none",
          borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font, marginRight: 6,
        }}>{saving ? "Saving…" : "Save"}</button>
        <button onClick={() => { setShowAddForm(false); setAddForm(emptyForm(type)); }} style={{
          padding: "4px 10px", background: "transparent", border: `1px solid ${colors.borderLight}`,
          color: colors.textMuted, borderRadius: 5, fontSize: 12, cursor: "pointer", fontFamily: font,
        }}>Cancel</button>
      </td>
    </tr>
  );

  const renderEditRow = (p) => (
    <tr>
      <td style={tdStyle}>
        <input style={inputStyle} value={editForm.program}
          onChange={(e) => setEditForm((f) => ({ ...f, program: e.target.value }))} />
      </td>
      <td style={tdStyle}>
        <select style={selectStyle} value={editForm.branch}
          onChange={(e) => setEditForm((f) => ({ ...f, branch: e.target.value }))}>
          <option value="">Branch...</option>
          {FRONT_DOOR_BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </td>
      {isBase && (
        <>
          <td style={tdStyle}>
            <input style={inputStyle} value={editForm.location || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))} />
          </td>
          <td style={tdStyle}>
            <input style={inputStyle} value={editForm.email || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
          </td>
          <td style={tdStyle}>
            <input style={inputStyle} value={editForm.phone || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
          </td>
        </>
      )}
      <td style={tdStyle}>
        <input style={inputStyle} value={editForm.link || ""}
          onChange={(e) => setEditForm((f) => ({ ...f, link: e.target.value }))} />
      </td>
      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        <button onClick={handleEditSave} disabled={saving} style={{
          padding: "4px 12px", background: colors.accent, color: "#fff", border: "none",
          borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font, marginRight: 6,
        }}>{saving ? "Saving…" : "Save"}</button>
        <button onClick={cancelEdit} style={{
          padding: "4px 10px", background: "transparent", border: `1px solid ${colors.borderLight}`,
          color: colors.textMuted, borderRadius: 5, fontSize: 12, cursor: "pointer", fontFamily: font,
        }}>Cancel</button>
      </td>
    </tr>
  );

  const renderDisplayRow = (p) => (
    <tr
      onClick={() => toggleExpand(p.id)}
      style={{
        cursor: "pointer",
        background: expandedIds.has(p.id) ? colors.surfaceAlt : "transparent",
      }}
    >
      <td style={{ ...tdStyle, fontWeight: 500 }}>
        <span style={{ marginRight: 6, fontSize: 10, opacity: 0.6 }}>
          {expandedIds.has(p.id) ? "▾" : "▸"}
        </span>
        {p.program}
      </td>
      <td style={tdStyle}>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
          background: colors.accentDim, color: colors.accent,
        }}>{p.branch}</span>
      </td>
      {isBase && (
        <>
          <td style={{ ...tdStyle, color: colors.textMuted }}>{p.location || "—"}</td>
          <td style={{ ...tdStyle, fontSize: 12 }}>
            {p.email ? <CopyEmail email={p.email} /> : <span style={{ color: colors.textMuted }}>—</span>}
          </td>
          <td style={{ ...tdStyle, color: colors.textMuted, fontSize: 12 }}>{p.phone || "—"}</td>
        </>
      )}
      <td style={tdStyle}>
        {p.link && /^https?:\/\//i.test(p.link) ? (
          <a href={p.link} target="_blank" rel="noopener noreferrer"
            style={{ color: colors.accent, fontSize: 12, textDecoration: "none" }}>
            Open ↗
          </a>
        ) : p.link ? (
          <span style={{ color: colors.textMuted, fontSize: 12 }}>{p.link}</span>
        ) : "—"}
      </td>
      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        <button
          onClick={(e) => { e.stopPropagation(); startEdit(p); }}
          style={{
            padding: "3px 10px", background: "transparent", border: `1px solid ${colors.borderLight}`,
            color: colors.textDim, borderRadius: 4, fontSize: 11, cursor: "pointer", fontFamily: font, marginRight: 6,
          }}
        >Edit</button>
        {user.role === "admin" && (
          <button
            onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${p.program}"?`)) onDelete(p.id); }}
            style={{
              padding: "3px 10px", background: "transparent", border: `1px solid rgba(239,68,68,0.3)`,
              color: colors.danger, borderRadius: 4, fontSize: 11, cursor: "pointer", fontFamily: font,
            }}
          >Delete</button>
        )}
      </td>
    </tr>
  );

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
      {/* Table header / controls */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "14px 18px",
        borderBottom: collapsed ? "none" : `1px solid ${colors.border}`, flexWrap: "wrap",
      }}>
        <button onClick={() => setCollapsed((c) => !c)} style={{
          background: "transparent", border: "none", cursor: "pointer", color: colors.textDim,
          fontSize: 16, padding: 0, lineHeight: 1, display: "flex", alignItems: "center",
        }}>
          {collapsed ? "▸" : "▾"}
        </button>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.text, flex: "0 0 auto" }}>{title}</h3>
        {!collapsed && (
          <>
            <input
              placeholder="Search programs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "6px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
                borderRadius: 6, color: colors.text, fontSize: 12, fontFamily: font, outline: "none", width: 180,
              }}
            />
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              style={{
                padding: "6px 10px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
                borderRadius: 6, color: colors.text, fontSize: 12, fontFamily: font, outline: "none",
              }}
            >
              <option value="all">All Branches</option>
              {FRONT_DOOR_BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <div style={{ flex: 1 }} />
            <button onClick={() => { if (showAddForm) setAddForm(emptyForm(type)); setShowAddForm((s) => !s); setEditingId(null); }} style={{
              padding: "7px 16px", background: showAddForm ? "transparent" : colors.accent,
              border: showAddForm ? `1px solid ${colors.borderLight}` : "none",
              color: showAddForm ? colors.textMuted : "#fff", borderRadius: 7,
              fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font,
            }}>
              {showAddForm ? "Cancel" : "+ Add Program"}
            </button>
          </>
        )}
      </div>

      {/* Table body */}
      {!collapsed && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Program", "Branch", ...(isBase ? ["Location", "Email", "Phone"] : []), "Link", "Actions"].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showAddForm && renderAddForm()}
              {filtered.length === 0 && !showAddForm && (
                <tr>
                  <td colSpan={isBase ? 7 : 4} style={{ padding: "28px 18px", textAlign: "center", color: colors.textDim, fontSize: 13 }}>
                    {programs.length === 0 ? "No programs yet. Add the first one above." : "No programs match your search."}
                  </td>
                </tr>
              )}
              {filtered.map((p) => (
                <React.Fragment key={p.id}>
                  {editingId === p.id ? renderEditRow(p) : renderDisplayRow(p)}
                  {expandedIds.has(p.id) && editingId !== p.id && (
                    <ProgramDetailPanel program={p} user={user} onSave={onSave} colSpan={isBase ? 7 : 4} />
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FrontDoorPrograms({ user }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.programs.list()
      .then((d) => { setPrograms(d || []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  const handleSave = async (item) => {
    try {
      await api.programs.save(item);
      setPrograms((prev) =>
        prev.some((p) => p.id === item.id)
          ? prev.map((p) => (p.id === item.id ? item : p))
          : [item, ...prev]
      );
    } catch (err) {
      console.error("Failed to save program:", err);
      throw err;
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.programs.delete(id);
      setPrograms((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Failed to delete program:", err);
      throw err;
    }
  };

  const orgPrograms = programs.filter((p) => p.type === "org");
  const basePrograms = programs.filter((p) => p.type === "base");

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: colors.textDim }}>Loading…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      <ProgramTable
        title="Organization Level Programs"
        type="org"
        programs={orgPrograms}
        user={user}
        onSave={handleSave}
        onDelete={handleDelete}
      />
      <ProgramTable
        title="Base Level Programs"
        type="base"
        programs={basePrograms}
        user={user}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}

// ─── PROGRAM DETAIL PANEL ───
function ProgramDetailPanel({ program, user, onSave, colSpan = 7 }) {
  const initContacts = () => {
    const kc = program.keyContacts || [];
    return [
      kc[0] || { name: "", email: "", phone: "" },
      kc[1] || { name: "", email: "", phone: "" },
    ];
  };

  const [contacts, setContacts] = useState(initContacts);
  const [contactsSaving, setContactsSaving] = useState(false);
  const [contactsSaved, setContactsSaved] = useState(false);
  const [engagementText, setEngagementText] = useState("");
  const [engagementSaving, setEngagementSaving] = useState(false);
  const [editingEngagementId, setEditingEngagementId] = useState(null);
  const [editingEngagementText, setEditingEngagementText] = useState("");
  const [deleteError, setDeleteError] = useState(null);
  const savedTimerRef = React.useRef(null);

  useEffect(() => {
    setContacts(initContacts());
  }, [program.id]);

  useEffect(() => {
    return () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); };
  }, []);

  const handleContactChange = (idx, field, value) => {
    setContacts((prev) => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    setContactsSaved(false);
  };

  const handleSaveContacts = async () => {
    setContactsSaving(true);
    try {
      const updated = { ...program, keyContacts: contacts };
      await onSave(updated);
      setContactsSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setContactsSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save contacts:", err);
    } finally {
      setContactsSaving(false);
    }
  };

  const handleAddEngagement = async () => {
    if (!engagementText.trim()) return;
    setEngagementSaving(true);
    try {
      const newEngagement = {
        id: generateId(),
        text: engagementText.trim(),
        addedBy: displayName(user),
        addedAt: new Date().toISOString(),
      };
      const updated = {
        ...program,
        engagements: [newEngagement, ...(program.engagements || [])],
      };
      await onSave(updated);
      setEngagementText("");
    } catch (err) {
      console.error("Failed to add engagement:", err);
    } finally {
      setEngagementSaving(false);
    }
  };

  const handleEditEngagement = (e) => {
    setEditingEngagementId(e.id);
    setEditingEngagementText(e.text);
  };

  const handleSaveEngagement = async (id) => {
    if (!editingEngagementText.trim()) return;
    try {
      const updated = {
        ...program,
        engagements: (program.engagements || []).map((e) =>
          e.id === id ? { ...e, text: editingEngagementText.trim() } : e
        ),
      };
      await onSave(updated);
      setEditingEngagementId(null);
      setEditingEngagementText("");
    } catch (err) {
      console.error("Failed to edit engagement:", err);
    }
  };

  const handleDeleteEngagement = async (id) => {
    if (!window.confirm("Delete this engagement?")) return;
    setDeleteError(null);
    try {
      const updated = {
        ...program,
        engagements: (program.engagements || []).filter((e) => e.id !== id),
      };
      await onSave(updated);
    } catch (err) {
      console.error("Failed to delete engagement:", err);
      setDeleteError("Failed to delete. Please try again.");
    }
  };

  const engagements = [...(program.engagements || [])].sort(
    (a, b) => new Date(a.addedAt) - new Date(b.addedAt)
  );

  const fieldInputStyle = {
    padding: "6px 10px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 5, color: colors.text, fontSize: 12, fontFamily: font, outline: "none", width: "100%",
    marginBottom: 6, boxSizing: "border-box",
  };

  const sectionLabel = {
    fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
    color: colors.textDim, marginBottom: 12,
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ padding: "20px 24px", background: colors.surfaceAlt }}>

          {/* Key Contacts */}
          <div style={{ marginBottom: 24 }}>
            <div style={sectionLabel}>Key Contacts</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {contacts.map((c, idx) => (
                <div key={idx} style={{
                  flex: "1 1 220px", background: colors.surface, border: `1px solid ${colors.border}`,
                  borderRadius: 8, padding: "14px 16px",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: colors.textDim, marginBottom: 10 }}>
                    Contact {idx + 1}
                  </div>
                  {["name", "email", "phone"].map((field) => (
                    <input
                      key={field}
                      style={fieldInputStyle}
                      placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                      value={c[field] || ""}
                      onChange={(e) => handleContactChange(idx, field, e.target.value)}
                    />
                  ))}
                </div>
              ))}
            </div>
            <button
              onClick={handleSaveContacts}
              disabled={contactsSaving}
              style={{
                marginTop: 12, padding: "7px 18px",
                background: contactsSaved ? colors.success : colors.accent,
                color: "#fff", border: "none", borderRadius: 6,
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font,
              }}
            >
              {contactsSaving ? "Saving…" : contactsSaved ? "Saved ✓" : "Save Contacts"}
            </button>
          </div>

          {/* Engagements */}
          <div>
            <div style={sectionLabel}>Engagements</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
              {deleteError && (
                <div style={{ color: colors.danger, fontSize: 12, marginBottom: 8 }}>{deleteError}</div>
              )}
              {engagements.length === 0 && (
                <div style={{ color: colors.textDim, fontSize: 12, fontStyle: "italic" }}>
                  No engagements yet.
                </div>
              )}
              {engagements.map((e) => (
                <div key={e.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: colors.surface, border: `1px solid ${colors.border}`,
                  borderRadius: 6, padding: "10px 14px",
                }}>
                  {editingEngagementId === e.id ? (
                    <>
                      <input
                        style={{
                          flex: 1, padding: "5px 10px", background: colors.bg,
                          border: `1px solid ${colors.borderLight}`, borderRadius: 5,
                          color: colors.text, fontSize: 13, fontFamily: font, outline: "none",
                        }}
                        value={editingEngagementText}
                        onChange={(ev) => setEditingEngagementText(ev.target.value)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter") handleSaveEngagement(e.id);
                          if (ev.key === "Escape") setEditingEngagementId(null);
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveEngagement(e.id)}
                        style={{
                          background: colors.accent, border: "none", color: "#fff",
                          borderRadius: 4, fontSize: 11, padding: "3px 10px",
                          cursor: "pointer", fontFamily: font, flexShrink: 0, fontWeight: 600,
                        }}
                      >Save</button>
                      <button
                        onClick={() => setEditingEngagementId(null)}
                        style={{
                          background: "transparent", border: `1px solid ${colors.borderLight}`,
                          color: colors.textDim, borderRadius: 4, fontSize: 11,
                          padding: "3px 8px", cursor: "pointer", fontFamily: font, flexShrink: 0,
                        }}
                      >Cancel</button>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: colors.text, marginBottom: 4 }}>{e.text}</div>
                        <div style={{ fontSize: 11, color: colors.textDim }}>
                          {e.addedBy} · {new Date(e.addedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditEngagement(e)}
                        style={{
                          background: "transparent", border: `1px solid ${colors.borderLight}`,
                          color: colors.textDim, borderRadius: 4, fontSize: 11,
                          padding: "2px 8px", cursor: "pointer", fontFamily: font, flexShrink: 0,
                        }}
                      >Edit</button>
                      <button
                        onClick={() => handleDeleteEngagement(e.id)}
                        style={{
                          background: "transparent", border: `1px solid rgba(239,68,68,0.3)`,
                          color: colors.danger, borderRadius: 4, fontSize: 11,
                          padding: "2px 8px", cursor: "pointer", fontFamily: font, flexShrink: 0,
                        }}
                      >Delete</button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{
                  flex: 1, padding: "6px 12px", background: colors.bg,
                  border: `1px solid ${colors.borderLight}`, borderRadius: 6,
                  color: colors.text, fontSize: 12, fontFamily: font, outline: "none",
                }}
                placeholder="Add an engagement note…"
                value={engagementText}
                onChange={(e) => setEngagementText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddEngagement(); }}
              />
              <button
                onClick={handleAddEngagement}
                disabled={!engagementText.trim() || engagementSaving}
                style={{
                  padding: "6px 16px", background: colors.accent, color: "#fff",
                  border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: font,
                  opacity: (!engagementText.trim() || engagementSaving) ? 0.5 : 1,
                }}
              >
                {engagementSaving ? "Adding…" : "Add"}
              </button>
            </div>
          </div>

        </div>
      </td>
    </tr>
  );
}

// ─── ADMIN PANEL ───
function AdminPanel({ users, currentUser, onUpdateUser, onRemoveUser }) {
  const isAdmin = currentUser.role === "admin";
  const handleHoursChange = async (u, val) => {
    const hrs = val === "" ? null : Number(val);
    const updated = { ...u, monthlyHoursAvailable: hrs };
    await api.users.save(updated);
    onUpdateUser(updated);
  };

  const handleRoleToggle = async (u) => {
    const newRole = u.role === "admin" ? "regular" : "admin";
    const updated = { ...u, role: newRole };
    await api.users.save(updated);
    onUpdateUser(updated);
  };

  const handleRemove = async (u) => {
    if (!window.confirm(`Remove ${displayName(u)} from the team? This deletes their user record. Their existing tasks and notes stay, but they will no longer appear as a team member.`)) return;
    try {
      await api.users.remove(u.id);
      onRemoveUser(u.id);
    } catch {
      window.alert("Couldn’t remove that user. Please try again.");
    }
  };

  const thStyle = { padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase", borderBottom: `1px solid ${colors.border}`, background: colors.surfaceAlt };

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700, color: colors.text }}>Team Members</h2>
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["", "Name", "Email", "Role", "Monthly Hrs Available", "Joined", ...(isAdmin ? [" "] : [])].map((h, i) => (
                <th key={i} style={{ ...thStyle, ...(h === "" ? { width: 48 } : {}) }}>{h.trim ? h.trim() : h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={{ padding: "8px 16px", borderBottom: `1px solid ${colors.border}`, width: 48 }}>
                  {u.photo ? (
                    <img src={u.photo} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", display: "block" }} />
                  ) : (
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%", background: colors.accentDim,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: colors.accent,
                    }}>{(u.firstName || u.name || "?")[0].toUpperCase()}</div>
                  )}
                </td>
                <td style={{ padding: "12px 16px", borderBottom: `1px solid ${colors.border}`, color: colors.text, fontSize: 13, fontWeight: 500 }}>
                  {displayName(u)} {u.id === currentUser.id && <span style={{ fontSize: 10, color: colors.accent }}>(you)</span>}
                  <div style={{ fontSize: 11, color: colors.textDim, marginTop: 1 }}>{fullName(u)}</div>
                </td>
                <td style={{ padding: "12px 16px", borderBottom: `1px solid ${colors.border}`, color: colors.textMuted, fontSize: 12, fontFamily: monoFont }}>{u.email}</td>
                <td style={{ padding: "12px 16px", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 5,
                      background: u.role === "admin" ? colors.accentDim : "rgba(100,116,139,0.15)",
                      color: u.role === "admin" ? colors.accent : colors.textMuted,
                    }}>{u.role}</span>
                    {u.id !== currentUser.id && (
                      <button
                        onClick={() => handleRoleToggle(u)}
                        style={{
                          fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                          background: "transparent", border: `1px solid ${colors.borderLight}`,
                          color: colors.textDim, fontFamily: font,
                        }}
                      >
                        {u.role === "admin" ? "Remove admin" : "Make admin"}
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ padding: "8px 16px", borderBottom: `1px solid ${colors.border}` }}>
                  <input
                    type="number"
                    defaultValue={u.monthlyHoursAvailable ?? ""}
                    onBlur={(e) => handleHoursChange(u, e.target.value)}
                    placeholder="—"
                    style={{
                      width: 72, padding: "6px 10px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
                      borderRadius: 6, color: colors.text, fontSize: 13, fontFamily: font, outline: "none", textAlign: "center",
                    }}
                  />
                </td>
                <td style={{ padding: "12px 16px", borderBottom: `1px solid ${colors.border}`, color: colors.textDim, fontSize: 12 }}>{formatDate(u.createdAt)}</td>
                {isAdmin && (
                  <td style={{ padding: "12px 16px", borderBottom: `1px solid ${colors.border}`, textAlign: "right" }}>
                    {u.id !== currentUser.id && (
                      <button
                        onClick={() => handleRemove(u)}
                        title="Remove user"
                        style={{
                          fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 4, cursor: "pointer",
                          background: "transparent", border: `1px solid ${colors.borderLight}`, color: colors.danger, fontFamily: font,
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── SETTINGS PAGE ───
function SettingsPage({ user, onUpdateUser }) {
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [monthlyHoursAvailable, setMonthlyHoursAvailable] = useState(user.monthlyHoursAvailable ?? "");
  const [photo, setPhoto] = useState(user.photo || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5000000) { setError("Photo must be under 5MB"); return; }
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 200;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      const min = Math.min(img.width, img.height);
      const sx = (img.width - min) / 2;
      const sy = (img.height - min) / 2;
      ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
      const compressed = canvas.toDataURL("image/jpeg", 0.8);
      setPhoto(compressed);
      setError("");
      setSaved(false);
    };
    img.src = URL.createObjectURL(file);
  };

  const handleRemovePhoto = () => { setPhoto(null); setSaved(false); };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) { setError("First and last name are required"); return; }
    setSaving(true);
    setError("");
    const updatedUser = { ...user, firstName: firstName.trim(), lastName: lastName.trim(), name: `${firstName.trim()} ${lastName.trim()}`, photo, monthlyHoursAvailable: monthlyHoursAvailable === "" ? null : Number(monthlyHoursAvailable) };
    await api.users.save(updatedUser);
    onUpdateUser(updatedUser);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 8, color: colors.text, fontSize: 14, fontFamily: font, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 8 };

  return (
    <div style={{ maxWidth: 520 }}>
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 32 }}>
        <h3 style={{ margin: "0 0 24px", fontSize: 16, fontWeight: 600, color: colors.text }}>Profile</h3>

        {/* Photo */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 28 }}>
          <div style={{ position: "relative" }}>
            {photo ? (
              <img src={photo} style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", display: "block" }} />
            ) : (
              <div style={{
                width: 72, height: 72, borderRadius: "50%", background: colors.accentDim,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 700, color: colors.accent,
              }}>{(firstName || user.firstName || "?")[0].toUpperCase()}</div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{
              padding: "7px 16px", background: colors.accentDim, border: `1px solid ${colors.accent}`,
              borderRadius: 6, color: colors.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-block",
            }}>
              {photo ? "Replace photo" : "Upload photo"}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
            </label>
            {photo && (
              <button onClick={handleRemovePhoto} style={{
                background: "transparent", border: "none", color: colors.danger, fontSize: 12,
                cursor: "pointer", fontFamily: font, padding: 0, textAlign: "left",
              }}>Remove photo</button>
            )}
          </div>
        </div>

        {/* Name fields */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>First Name</label>
            <input value={firstName} onChange={(e) => { setFirstName(e.target.value); setSaved(false); }} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Last Name</label>
            <input value={lastName} onChange={(e) => { setLastName(e.target.value); setSaved(false); }} style={inputStyle} />
          </div>
        </div>

        {/* Monthly Hours Available */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Monthly Hours Available</label>
          <input
            type="number"
            value={monthlyHoursAvailable}
            onChange={(e) => { setMonthlyHoursAvailable(e.target.value); setSaved(false); }}
            placeholder="e.g. 80"
            style={{ ...inputStyle, maxWidth: 160 }}
          />
          <div style={{ fontSize: 11, color: colors.textDim, marginTop: 6 }}>Used to show utilization on the Ops Planner timeline</div>
        </div>

        {/* Email (read-only) */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Email</label>
          <div style={{
            padding: "12px 14px", background: colors.bg, border: `1px solid ${colors.border}`,
            borderRadius: 8, color: colors.textDim, fontSize: 14, fontFamily: monoFont,
          }}>{user.email}</div>
        </div>

        {error && <div style={{ color: colors.danger, fontSize: 13, marginBottom: 16, padding: "8px 12px", background: "#3b1c1c", borderRadius: 6 }}>{error}</div>}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "10px 24px", background: colors.accent, border: "none", borderRadius: 8,
            color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: font,
            opacity: saving ? 0.6 : 1,
          }}>{saving ? "Saving..." : "Save Changes"}</button>
          {saved && <span style={{ fontSize: 13, color: colors.success, fontWeight: 500 }}>✓ Saved</span>}
        </div>
      </div>

      {/* Account Info */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 32, marginTop: 20 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600, color: colors.text }}>Account</h3>
        <div style={{ display: "flex", gap: 24 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Role</div>
            <span style={{
              fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 5,
              background: user.role === "admin" ? colors.accentDim : "rgba(100,116,139,0.15)",
              color: user.role === "admin" ? colors.accent : colors.textMuted,
            }}>{user.role}</span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Member Since</div>
            <div style={{ fontSize: 13, color: colors.text }}>{formatDate(user.createdAt)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── OPS PLANNER ───
function OpsPlanner({ user, users }) {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]); // client registry
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | project object
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortField, setSortField] = useState("client");
  const [sortDir, setSortDir] = useState("asc");
  const [showArchived, setShowArchived] = useState(false);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  // Archived projects (additive archivedAt flag) drop out of the table's main list,
  // the Gantt, and utilization — surfaced only in a collapsed "Archived" section.
  const liveProjects = useMemo(() => projects.filter((p) => !p.archivedAt), [projects]);
  const archivedProjects = useMemo(() => projects.filter((p) => p.archivedAt), [projects]);

  const sortedProjects = useMemo(() => {
    return [...liveProjects].sort((a, b) => {
      // Client column sorts on the resolved registry name, not the id/legacy string.
      const resolve = (p) => clients.find((c) => c.id === p.clientId)?.name || p.client || "";
      const va = sortField === "client" ? resolve(a) : (a[sortField] || "");
      const vb = sortField === "client" ? resolve(b) : (b[sortField] || "");
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [liveProjects, sortField, sortDir, clients]);

  // Resolve a project's client for display: registry name by clientId, else the legacy string.
  const clientName = (p) => clients.find((c) => c.id === p.clientId)?.name || p.client || "—";

  useEffect(() => {
    api.opsplanner.list()
      .then((d) => { setProjects(d || []); setLoading(false); })
      .catch((e) => { setLoadError(e.message); setLoading(false); });
    api.clients.list().then((c) => setClients(c || [])).catch(() => setClients([]));
  }, []);

  const shortName = (userId) => {
    const u = users.find((x) => x.id === userId || x.email === userId);
    if (!u) return "—";
    const first = u.firstName || u.name?.split(" ")[0] || "";
    const last = u.lastName || u.name?.split(" ").slice(-1)[0] || "";
    return last ? `${first} ${last[0]}.` : first || "—";
  };

  const handleSave = async (project) => {
    const saved = { ...project, updatedAt: new Date().toISOString() };
    if (!projects.find((p) => p.id === project.id)) saved.createdAt = new Date().toISOString();
    await api.opsplanner.save(saved);
    setProjects((prev) => {
      const exists = prev.find((p) => p.id === saved.id);
      return exists ? prev.map((p) => (p.id === saved.id ? saved : p)) : [...prev, saved];
    });
    setModal(null);
  };

  const handleDelete = async (id) => {
    await api.opsplanner.delete(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
    setDeleteConfirm(null);
  };

  const handleInlineAssign = async (projectId, field, userId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const updated = { ...project, [field]: userId, updatedAt: new Date().toISOString() };
    await api.opsplanner.save(updated);
    setProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
  };

  // Tier lives on the client, so every project row for that client changes together.
  const handleTierChange = async (clientId, value) => {
    if (!clientId) return;
    const tier = value === "" ? null : Number(value);
    const before = clients.find((c) => c.id === clientId)?.tier ?? null;
    setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, tier } : c)));
    const res = await api.clients.update(clientId, { tier });
    if (!res.ok) {
      setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, tier: before } : c)));
      window.alert(res.data?.error || "Couldn’t save the tier — try again.");
    }
  };

  // Archive is an additive flag (never a delete): stamp who + when, or clear to restore.
  const toggleArchive = async (projectId, archived) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const now = new Date().toISOString();
    const updated = archived
      ? { ...project, archivedAt: now, archivedBy: user?.email || "system", updatedAt: now }
      : { ...project, archivedAt: null, archivedBy: null, updatedAt: now };
    await api.opsplanner.save(updated);
    setProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
  };

  const thStyle = {
    padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: colors.textDim,
    letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap",
    borderBottom: `1px solid ${colors.border}`, background: colors.surfaceAlt,
  };
  const tdStyle = { padding: "12px 14px", borderBottom: `1px solid ${colors.border}`, fontSize: 13, color: colors.text, verticalAlign: "middle" };

  // ── Timeline ──
  const today = new Date();
  const ganttStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
  const ganttMonths = [];
  for (let i = 0; i < 12; i++) {
    ganttMonths.push(new Date(ganttStart.getFullYear(), ganttStart.getMonth() + i, 1));
  }

  // Percentage based on equal month widths (12 columns) for perfect alignment
  const toMonthPct = (date) => {
    const d = new Date(Math.max(ganttStart, Math.min(new Date(ganttStart.getFullYear(), ganttStart.getMonth() + 12, 0), date)));
    const yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
    const daysInMonth = new Date(yr, mo + 1, 0).getDate();
    const months = (yr - ganttStart.getFullYear()) * 12 + (mo - ganttStart.getMonth()) + (day - 1) / daysInMonth;
    return (Math.min(months, 12) / 12) * 100;
  };

  const PROJECT_COLORS = [
    { bar: "#3b82f6", bg: "rgba(59,130,246,0.18)" },
    { bar: "#10b981", bg: "rgba(16,185,129,0.18)" },
    { bar: "#8b5cf6", bg: "rgba(139,92,246,0.18)" },
    { bar: "#f59e0b", bg: "rgba(245,158,11,0.18)" },
    { bar: "#ef4444", bg: "rgba(239,68,68,0.18)" },
    { bar: "#14b8a6", bg: "rgba(20,184,166,0.18)" },
    { bar: "#f472b6", bg: "rgba(244,114,182,0.18)" },
  ];

  const getMonthHours = (pmId, month) => {
    const ms = month;
    const me = new Date(month.getFullYear(), month.getMonth() + 1, 0); // last day of month
    const daysInMonth = me.getDate();
    const matchesPm = (p) => pmId === "__unassigned__" ? !p.projectManager : p.projectManager === pmId;
    return liveProjects
      .filter((p) => matchesPm(p) && p.startDate && p.endDate &&
        new Date(p.startDate + "T00:00:00") <= me && new Date(p.endDate + "T00:00:00") >= ms)
      .reduce((s, p) => {
        const ps = new Date(p.startDate + "T00:00:00");
        const pe = new Date(p.endDate + "T00:00:00");
        const activeStart = ps > ms ? ps : ms;
        const activeEnd = pe < me ? pe : me;
        const activeDays = Math.round((activeEnd - activeStart) / 86400000) + 1;
        const fraction = Math.min(activeDays / daysInMonth, 1);
        return s + (Number(p.hoursPerMonth) || 0) * fraction;
      }, 0);
  };

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading...</div>;
  if (loadError) return <div style={{ padding: 40, color: colors.danger }}>Failed to load projects: {loadError}</div>;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
        <button
          onClick={() => setModal("new")}
          style={{
            padding: "9px 20px", background: colors.accent, border: "none", borderRadius: 8,
            color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font,
          }}
        >+ New Project</button>
      </div>

      {/* Table */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 32 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                {[
                  { label: "Client", field: "client" },
                  { label: "Tier", field: null },
                  { label: "Project Type", field: "projectType" },
                  { label: "Start Date", field: "startDate" },
                  { label: "End Date", field: "endDate" },
                  { label: "Hrs / Month", field: null },
                  { label: "Account Owner", field: null },
                  { label: "Project Manager", field: null },
                  { label: "", field: null },
                ].map((col) => (
                  <th key={col.label} style={{ ...thStyle, cursor: col.field ? "pointer" : "default" }}
                    onClick={() => col.field && toggleSort(col.field)}>
                    {col.label}{col.field && sortField === col.field ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedProjects.length === 0 ? (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: "center", padding: 40, color: colors.textDim }}>{projects.length === 0 ? "No projects yet — add one above" : "No active projects — see Archived below"}</td></tr>
              ) : (
                sortedProjects.map((p) => (
                  <tr key={p.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{clientName(p)}</td>
                    <td style={{ ...tdStyle, padding: "8px 14px" }}>
                      <select
                        aria-label={`Tier for ${clientName(p)}`}
                        value={clients.find((c) => c.id === p.clientId)?.tier ?? ""}
                        disabled={!p.clientId}
                        title={p.clientId ? "Applies to every project for this client" : "Link this project to a client to set a tier"}
                        onChange={(e) => handleTierChange(p.clientId, e.target.value)}
                        style={{
                          background: colors.bg, border: `1px solid ${colors.borderLight}`, borderRadius: 6,
                          color: clients.find((c) => c.id === p.clientId)?.tier ? colors.text : colors.textDim, fontSize: 12,
                          fontFamily: font, padding: "5px 8px", cursor: p.clientId ? "pointer" : "not-allowed", outline: "none",
                        }}
                      >
                        <option value="">—</option>
                        {TIERS.map((t) => <option key={t} value={t}>Tier {t}</option>)}
                      </select>
                    </td>
                    <td style={{ ...tdStyle, color: colors.textMuted }}>
                      {p.projectType === "proposal-plan" ? "6-Month Proposal Plan" : p.customTitle || p.projectType || "—"}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{p.startDate ? new Date(p.startDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{p.endDate ? new Date(p.endDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                    <td style={{ ...tdStyle, fontFeatureSettings: '"tnum"' }}>{p.hoursPerMonth || "—"}</td>
                    <td style={{ ...tdStyle, padding: "8px 14px" }}>
                      <select
                        value={p.accountOwner ?? p.projectOversight ?? ""}
                        onChange={(e) => handleInlineAssign(p.id, "accountOwner", e.target.value)}
                        style={{
                          background: colors.bg, border: `1px solid ${colors.borderLight}`, borderRadius: 6,
                          color: (p.accountOwner ?? p.projectOversight) ? colors.text : colors.textDim, fontSize: 12,
                          fontFamily: font, padding: "5px 8px", cursor: "pointer", outline: "none",
                        }}
                      >
                        <option value="">— Unassigned —</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{shortName(u.id)}</option>)}
                      </select>
                    </td>
                    <td style={{ ...tdStyle, padding: "8px 14px" }}>
                      <select
                        value={p.projectManager || ""}
                        onChange={(e) => handleInlineAssign(p.id, "projectManager", e.target.value)}
                        style={{
                          background: colors.bg, border: `1px solid ${colors.borderLight}`, borderRadius: 6,
                          color: p.projectManager ? colors.text : colors.textDim, fontSize: 12,
                          fontFamily: font, padding: "5px 8px", cursor: "pointer", outline: "none",
                        }}
                      >
                        <option value="">— Unassigned —</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{shortName(u.id)}</option>)}
                      </select>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <button onClick={() => setModal(p)} style={{ background: "none", border: "none", color: colors.textMuted, cursor: "pointer", fontSize: 12, fontFamily: font, marginRight: 8 }}>Edit</button>
                      <button onClick={() => toggleArchive(p.id, true)} style={{ background: "none", border: "none", color: colors.textMuted, cursor: "pointer", fontSize: 12, fontFamily: font, marginRight: 8 }}>Archive</button>
                      <button onClick={() => setDeleteConfirm(p.id)} style={{ background: "none", border: "none", color: colors.danger, cursor: "pointer", fontSize: 12, fontFamily: font }}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Archived projects — collapsed; additive archivedAt flag, excluded from table/Gantt/utilization */}
      {archivedProjects.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <button onClick={() => setShowArchived((s) => !s)} style={{
            background: "none", border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted,
            fontSize: 13, fontWeight: 600, fontFamily: font, cursor: "pointer", padding: "8px 14px",
          }}>
            <span style={{ display: "inline-block", transform: showArchived ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .15s" }}>▶</span>
            {" "}Archived ({archivedProjects.length})
          </button>
          {showArchived && (
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden", marginTop: 10 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                  <tbody>
                    {archivedProjects.map((p) => (
                      <tr key={p.id}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{clientName(p)}</td>
                        <td style={{ ...tdStyle, color: colors.textMuted }}>{p.projectType === "proposal-plan" ? "6-Month Proposal Plan" : p.customTitle || p.projectType || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: colors.textDim }}>
                          Archived {p.archivedAt ? new Date(p.archivedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}{p.archivedBy ? ` · ${p.archivedBy}` : ""}
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap", textAlign: "right" }}>
                          <button onClick={() => toggleArchive(p.id, false)} style={{ background: "none", border: "none", color: colors.accent, cursor: "pointer", fontSize: 12, fontFamily: font, marginRight: 8 }}>Unarchive</button>
                          <button onClick={() => setDeleteConfirm(p.id)} style={{ background: "none", border: "none", color: colors.danger, cursor: "pointer", fontSize: 12, fontFamily: font }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Unified PM Timeline */}
      {(() => {
        const PHASES = [
          { label: "Onboarding", color: "#8b5cf6", bg: "rgba(139,92,246,0.2)" },
          { label: "Outreach Campaign", color: "#14b8a6", bg: "rgba(20,184,166,0.2)" },
          { label: "Proposals", color: "#3b82f6", bg: "rgba(59,130,246,0.2)" },
        ];

        // Color by monthly hours (on the rounded value shown in the cell):
        // 130–160 = green, 100–129 = yellow, ≥161 or ≤99 = red.
        const cellColor = (hrs) => {
          if (!hrs) return { bg: "transparent", text: colors.textDim };
          const h = Math.round(hrs);
          if (h >= 161) return { bg: "rgba(239,68,68,0.18)", text: "#ef4444" };      // 161+ — red
          if (h >= 130) return { bg: "rgba(16,185,129,0.15)", text: "#10b981" };     // 130–160 — green
          if (h >= 100) return { bg: "rgba(234,179,8,0.15)", text: "#eab308" };      // 100–129 — yellow
          return { bg: "rgba(239,68,68,0.18)", text: "#ef4444" };                     // ≤99 — red
        };

        // Group projects by PM; sort PMs alphabetically, unassigned always last
        const pmIds = [...new Set(liveProjects.map((p) => p.projectManager || "__unassigned__"))];
        const grouped = pmIds.map((pmId) => ({
          pmId,
          projects: liveProjects
            .filter((p) => (p.projectManager || "__unassigned__") === pmId && p.startDate && p.endDate)
            .sort((a, b) => (a.startDate || "") < (b.startDate || "") ? -1 : 1),
        })).filter((g) => g.projects.length > 0)
          .sort((a, b) => {
            if (a.pmId === "__unassigned__") return 1;
            if (b.pmId === "__unassigned__") return -1;
            return shortName(a.pmId).localeCompare(shortName(b.pmId));
          });

        // Max hours across all PM/month combos for color scaling
        let maxHrs = 0;
        pmIds.filter((id) => id !== "__unassigned__").forEach((pmId) => {
          ganttMonths.forEach((m) => { const h = getMonthHours(pmId, m); if (h > maxHrs) maxHrs = h; });
        });

        const todayPct = toMonthPct(today);
        const LABEL_W = 200;
        const thCell = {
          fontSize: 10, fontWeight: 600, color: colors.textDim, letterSpacing: 0.8,
          textTransform: "uppercase", background: colors.surfaceAlt, textAlign: "center",
          padding: "10px 2px", whiteSpace: "nowrap", flex: 1, borderRight: `1px solid ${colors.border}`,
        };

        return (
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden", marginTop: 32 }}>
            {/* Header */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.5, color: colors.textDim, textTransform: "uppercase" }}>PM Project Timeline</div>
              <div style={{ display: "flex", gap: 16, fontSize: 11, color: colors.textDim }}>
                {[{ label: "≤99 hrs", color: "#ef4444", bg: "rgba(239,68,68,0.15)" }, { label: "100–129 hrs", color: "#eab308", bg: "rgba(234,179,8,0.15)" }, { label: "130–160 hrs", color: "#10b981", bg: "rgba(16,185,129,0.15)" }, { label: "161+ hrs", color: "#ef4444", bg: "rgba(239,68,68,0.15)" }].map((l) => (
                  <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: l.bg, border: `1px solid ${l.color}`, display: "inline-block" }} />
                    {l.label}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 900 }}>
                {/* Month header row */}
                <div style={{ display: "flex", borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ width: LABEL_W, flexShrink: 0, background: colors.surfaceAlt, padding: "10px 16px", fontSize: 10, fontWeight: 600, color: colors.textDim, letterSpacing: 0.8, textTransform: "uppercase" }}>Project Manager</div>
                  <div style={{ flex: 1, display: "flex" }}>
                    {ganttMonths.map((m, i) => {
                      const isNow = m.getMonth() === today.getMonth() && m.getFullYear() === today.getFullYear();
                      return (
                        <div key={i} style={{ ...thCell, color: isNow ? colors.accent : colors.textDim, borderBottom: isNow ? `2px solid ${colors.accent}` : undefined }}>
                          {m.toLocaleDateString("en-US", { month: "short", year: "2-digit" })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {grouped.length === 0 ? (
                  <div style={{ padding: "40px 20px", textAlign: "center", color: colors.textDim, fontSize: 13 }}>No projects with dates yet — add a project above</div>
                ) : (
                  grouped.map(({ pmId, projects: pmProjects }) => {
                    const isUnassigned = pmId === "__unassigned__";
                    const colorIdx = pmIds.filter((id) => id !== "__unassigned__").indexOf(pmId);
                    const pmColor = PROJECT_COLORS[colorIdx % PROJECT_COLORS.length];

                    return (
                      <div key={pmId} style={{ borderBottom: `2px solid ${colors.border}` }}>
                        {/* PM hours row */}
                        {(() => {
                          const pmUser = users.find((u) => u.id === pmId);
                          const capacity = pmUser?.monthlyHoursAvailable || null;
                          return (
                            <div style={{ display: "flex", background: colors.surfaceAlt }}>
                              <div style={{
                                width: LABEL_W, flexShrink: 0, padding: "10px 16px",
                                fontSize: 13, fontWeight: 700, color: isUnassigned ? colors.textDim : colors.text,
                                display: "flex", alignItems: "center", gap: 8,
                              }}>
                                {!isUnassigned && <span style={{ width: 8, height: 8, borderRadius: "50%", background: pmColor.bar, flexShrink: 0, display: "inline-block" }} />}
                                <div>
                                  <div>{isUnassigned ? "Unassigned" : shortName(pmId)}</div>
                                  {capacity && <div style={{ fontSize: 10, color: colors.textDim, fontWeight: 400 }}>{capacity} hrs available</div>}
                                </div>
                              </div>
                              <div style={{ flex: 1, display: "flex" }}>
                                {ganttMonths.map((m, i) => {
                                  const hrs = getMonthHours(isUnassigned ? "__unassigned__" : pmId, m);
                                  const c = isUnassigned ? { bg: "transparent", text: colors.textMuted } : cellColor(hrs);
                                  const activeProjects = pmProjects.filter((p) => {
                                    const ps = new Date(p.startDate + "T00:00:00"), pe = new Date(p.endDate + "T00:00:00");
                                    const me = new Date(m.getFullYear(), m.getMonth() + 1, 0);
                                    return ps <= me && pe >= m;
                                  });
                                  const tooltip = [
                                    ...activeProjects.map((p) => `${clientName(p)} (${p.hoursPerMonth}h)`),
                                    !isUnassigned && capacity ? `${Math.round(hrs)}/${capacity} hrs used` : "",
                                  ].filter(Boolean).join(" · ");
                                  return (
                                    <div key={i} title={tooltip || undefined} style={{
                                      flex: 1, textAlign: "center", padding: "6px 2px",
                                      fontSize: 11, fontWeight: hrs ? 700 : 400,
                                      color: hrs ? c.text : colors.textDim,
                                      background: c.bg,
                                      borderRight: `1px solid ${colors.border}`,
                                      fontFeatureSettings: '"tnum"',
                                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                    }}>
                                      <div>{hrs ? Math.round(hrs) : "—"}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Project Gantt rows — one row per client under this PM; a client's
                            projects share the row. Overlapping projects split it into lanes. */}
                        {(() => {
                          const rows = [];
                          pmProjects.forEach((p) => {
                            const key = p.clientId || (p.client ? "name:" + p.client.trim().toLowerCase() : "id:" + p.id);
                            let row = rows.find((r) => r.key === key);
                            if (!row) { row = { key, projects: [] }; rows.push(row); }
                            row.projects.push(p);
                          });
                          return rows.map((row) => {
                            // Greedy lane assignment (pmProjects is already sorted by startDate).
                            const laneEnds = [];
                            const placed = row.projects.map((p) => {
                              let lane = laneEnds.findIndex((e) => e < p.startDate);
                              if (lane === -1) { lane = laneEnds.length; laneEnds.push(p.endDate); } else laneEnds[lane] = p.endDate;
                              return { p, lane };
                            });
                            const lanes = laneEnds.length;
                            const barH = 60 / lanes;
                            return (
                              <div key={row.key} style={{ display: "flex", height: lanes > 1 ? 22 * lanes + 8 : 40, borderBottom: `1px solid ${colors.border}` }}>
                                <div style={{
                                  width: LABEL_W, flexShrink: 0, padding: "0 16px 0 28px",
                                  display: "flex", alignItems: "center",
                                  fontSize: 12, color: colors.text, overflow: "hidden",
                                }}>
                                  <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{clientName(row.projects[0])}</span>
                                </div>
                                <div style={{ flex: 1, position: "relative" }}>
                                  {/* Today line */}
                                  <div style={{ position: "absolute", left: `${todayPct}%`, top: 0, bottom: 0, width: 2, background: colors.accent, opacity: 0.5, zIndex: 1, pointerEvents: "none" }} />
                                  {/* Month grid lines */}
                                  {ganttMonths.map((m, i) => (
                                    <div key={i} style={{ position: "absolute", left: `${(i / 12) * 100}%`, top: 0, bottom: 0, width: 1, background: colors.border, pointerEvents: "none" }} />
                                  ))}
                                  {placed.map(({ p, lane }) => {
                                    const start = new Date(p.startDate + "T00:00:00");
                                    const end = new Date(p.endDate + "T00:00:00");
                                    const CUSTOM_COLOR = { bar: "#f97316", bg: "rgba(249,115,22,0.18)" };
                                    const barColor = p.projectType === "proposal-plan" ? pmColor : CUSTOM_COLOR;
                                    const top = `${20 + lane * barH}%`;
                                    const height = `${barH - (lanes > 1 ? 2 : 0)}%`;

                                    if (p.projectType === "proposal-plan") {
                                      const phaseStarts = [
                                        start,
                                        new Date(start.getFullYear(), start.getMonth() + 1, start.getDate()),
                                        new Date(start.getFullYear(), start.getMonth() + 3, start.getDate()),
                                      ];
                                      const phaseEnds = [phaseStarts[1], phaseStarts[2], end];
                                      return PHASES.map((phase, i) => {
                                        const leftPct = toMonthPct(phaseStarts[i]);
                                        const widthPct = Math.max(0.5, toMonthPct(phaseEnds[i]) - leftPct);
                                        return (
                                          <div key={p.id + ":" + i} style={{
                                            position: "absolute", left: `${leftPct}%`, width: `${widthPct}%`,
                                            top, height,
                                            background: phase.bg, border: `1px solid ${phase.color}`,
                                            borderRadius: i === 0 ? "3px 0 0 3px" : i === 2 ? "0 3px 3px 0" : "0",
                                            borderLeft: i > 0 ? "none" : undefined,
                                            display: "flex", alignItems: "center", overflow: "hidden", minWidth: 4,
                                          }}>
                                            <span style={{ fontSize: 9, fontWeight: 700, color: phase.color, paddingLeft: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase", letterSpacing: 0.4 }}>{phase.label}</span>
                                          </div>
                                        );
                                      });
                                    }
                                    return (
                                      <div key={p.id} style={{
                                        position: "absolute", left: `${toMonthPct(start)}%`,
                                        width: `${Math.max(0.5, toMonthPct(end) - toMonthPct(start))}%`,
                                        top, height,
                                        background: barColor.bg, border: `1px solid ${barColor.bar}`,
                                        borderRadius: 3, display: "flex", alignItems: "center", overflow: "hidden", minWidth: 4,
                                      }}>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: barColor.bar, paddingLeft: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {p.customTitle || "Custom"}{p.hoursPerMonth ? ` · ${p.hoursPerMonth}h/mo` : ""}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add/Edit Modal */}

      {/* Add/Edit Modal */}
      {modal && (
        <OpsPlannerModal
          project={modal === "new" ? null : modal}
          users={users}
          clients={clients}
          onClientCreated={(c) => setClients((prev) => (prev.find((x) => x.id === c.id) ? prev : [...prev, c]))}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000,
        }} onClick={() => setDeleteConfirm(null)}>
          <div style={{
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12,
            padding: 28, width: 360, textAlign: "center",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 8 }}>Delete Project?</div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 24 }}>This cannot be undone.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: "9px 20px", background: "transparent", border: `1px solid ${colors.borderLight}`, borderRadius: 8, color: colors.textMuted, fontSize: 13, cursor: "pointer", fontFamily: font }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: "9px 20px", background: colors.danger, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OpsPlannerModal({ project, users, clients, onClientCreated, onSave, onClose }) {
  const defaultType = project?.projectType || "proposal-plan";
  const [form, setForm] = useState(project
    ? { ...project, accountOwner: project.accountOwner ?? project.projectOversight ?? "" }
    : {
        id: generateId(),
        client: "",
        clientId: "",
        projectType: "proposal-plan",
        customTitle: "",
        startDate: "",
        endDate: "",
        hoursPerMonth: "34",
        accountOwner: "",
        projectManager: "",
      });

  const update = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const selectType = (type) => {
    setForm((prev) => ({
      ...prev,
      projectType: type,
      hoursPerMonth: type === "proposal-plan" ? "34" : prev.hoursPerMonth,
    }));
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 6, color: colors.text, fontSize: 14, fontFamily: font, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 6 };
  const fieldWrap = { marginBottom: 16 };

  const isProposalPlan = form.projectType === "proposal-plan";
  const isValid = (form.clientId || form.client) && form.startDate && form.endDate && (isProposalPlan || form.customTitle);

  const typeCardStyle = (active) => ({
    flex: 1, padding: "12px 16px", borderRadius: 8, cursor: "pointer", fontFamily: font,
    border: `2px solid ${active ? colors.accent : colors.borderLight}`,
    background: active ? colors.accentDim : colors.bg,
    transition: "all 0.15s ease",
  });

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        width: 580, maxHeight: "85vh", overflow: "auto", background: colors.surface,
        border: `1px solid ${colors.border}`, borderRadius: 16, padding: 32,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.text }}>{project ? "Edit Project" : "New Project"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: colors.textDim, fontSize: 24, cursor: "pointer" }}>×</button>
        </div>

        {/* Project Type Selection */}
        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Project Type *</div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => selectType("proposal-plan")} style={typeCardStyle(isProposalPlan)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isProposalPlan ? colors.accent : colors.borderLight}`,
                  background: isProposalPlan ? colors.accent : "transparent", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isProposalPlan && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: isProposalPlan ? colors.text : colors.textMuted }}>6-Month Proposal Plan</span>
              </div>
              <div style={{ fontSize: 11, color: colors.textDim, paddingLeft: 24 }}>3 phases · 40 hrs/mo · auto-phased timeline</div>
            </button>
            <button onClick={() => selectType("custom")} style={typeCardStyle(!isProposalPlan)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: "50%", border: `2px solid ${!isProposalPlan ? colors.accent : colors.borderLight}`,
                  background: !isProposalPlan ? colors.accent : "transparent", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {!isProposalPlan && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: !isProposalPlan ? colors.text : colors.textMuted }}>Custom Project</span>
              </div>
              <div style={{ fontSize: 11, color: colors.textDim, paddingLeft: 24 }}>Custom title and hours</div>
            </button>
          </div>
        </div>

        {/* Custom title */}
        {!isProposalPlan && (
          <div style={{ ...fieldWrap }}>
            <label style={labelStyle}>Project Title *</label>
            <input style={inputStyle} value={form.customTitle} onChange={(e) => update("customTitle", e.target.value)} placeholder="Enter project title..." />
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ ...fieldWrap, gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Client *</label>
            <ClientPicker
              value={form.clientId || ""}
              clients={clients || []}
              onClientCreated={onClientCreated}
              onChange={(id) => setForm((prev) => ({ ...prev, clientId: id, client: "" }))}
              legacyName={!form.clientId ? form.client : undefined}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Start Date *</label>
            <input style={inputStyle} type="date" value={form.startDate} onChange={(e) => update("startDate", e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>End Date *</label>
            <input style={inputStyle} type="date" value={form.endDate} onChange={(e) => update("endDate", e.target.value)} />
          </div>
          <div style={{ ...fieldWrap, gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Hours per Month</label>
            <input
              style={inputStyle}
              type="number" value={form.hoursPerMonth}
              onChange={(e) => update("hoursPerMonth", e.target.value)}
              placeholder="0"
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Account Owner</label>
            <select style={inputStyle} value={form.accountOwner || ""} onChange={(e) => update("accountOwner", e.target.value)}>
              <option value="">Select team member</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name || u.email}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Project Manager</label>
            <select style={inputStyle} value={form.projectManager} onChange={(e) => update("projectManager", e.target.value)}>
              <option value="">Select team member</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name || u.email}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onClose} style={{
            padding: "10px 20px", background: "transparent", border: `1px solid ${colors.borderLight}`,
            borderRadius: 8, color: colors.textMuted, fontSize: 14, cursor: "pointer", fontFamily: font,
          }}>Cancel</button>
          <button
            onClick={() => { if (isValid) onSave(form); }}
            style={{
              padding: "10px 24px", background: colors.accent, border: "none", borderRadius: 8,
              color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: font,
              opacity: isValid ? 1 : 0.4,
            }}
          >{project ? "Update Project" : "Add Project"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ───
export default function SawtoothOpsApp() {
  const { user: authUser, isAuthenticated, loading: authLoading, login, logout } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  // Set when we're authenticated per MSAL but the DB user can't be loaded (e.g. a stale
  // session — AADSTS160021 — makes the API token unacquirable and every /api call 401s).
  // Without this we'd sit on the "Signing you in…" spinner forever; instead we fall back to
  // the sign-in screen so the user can re-authenticate interactively.
  const [userLoadError, setUserLoadError] = useState(false);
  const [users, setUsers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [archived, setArchived] = useState([]);
  const [calEvents, setCalEvents] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [page, setPage] = useState(() => {
    try {
      const h = window.location.hash;
      return /client=/.test(h) ? "clients" : /^#tasks(&|$)/.test(h) ? "employees" : /^#internal(&|$)/.test(h) ? "internal" : "home";
    } catch { return "home"; }
  });
  // Menu navigation clears any open-client hash (#client=<id>) so returning to Clients shows
  // the grid rather than reopening the last client. Deep-links on first load are preserved.
  const navigate = useCallback((id) => {
    try { if (/client=|^#tasks|^#internal/.test(window.location.hash)) window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* ignore */ }
    setPage(id);
  }, []);

  // Map MSAL user to app user (with role from DB)
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!authUser || !isAuthenticated) { setUser(null); return; }
    setUserLoadError(false);
    // Check if user exists in DB, if not create them
    api.users.list().then((dbUsers) => {
      const existing = dbUsers?.find((u) => u.email === authUser.email);
      if (existing) {
        setUser(existing);
      } else {
        const newUser = {
          id: authUser.id || generateId(),
          email: authUser.email,
          firstName: authUser.firstName,
          lastName: authUser.lastName,
          name: authUser.name,
          // New users are always "regular". Admins are seeded explicitly (see
          // docs/manual-steps.md §6); never auto-promote based on table emptiness —
          // an empty/mis-restored users table must not hand out admin.
          role: "regular",
          createdAt: new Date().toISOString(),
        };
        return api.users.save(newUser).then(() => setUser(newUser));
      }
    }).catch(() => {
      // Token/session problem (stale session → 401). Surface the sign-in screen so the
      // user can re-authenticate, rather than spinning on "Signing you in…" forever.
      setUserLoadError(true);
    });
  }, [authUser, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    // One refresh of every polled collection — used for the initial load and each
    // poll tick. Wrapped so a transient failure logs instead of surfacing as an
    // unhandled promise rejection.
    const refresh = async () => {
      try {
        const [p, a, e, u, d] = await Promise.all([
          api.proposals.list(), api.archived.list(),
          api.events.list(), api.users.list(),
          api.discussions.list(),
        ]);
        if (cancelled) return;
        setProposals(p || []); setArchived(a || []); setCalEvents(e || []);
        setUsers(u || []); setDiscussions(d || []);
      } catch (err) {
        console.warn("Background refresh failed:", err?.message || err);
      }
    };

    refresh(); // initial load

    // Poll only while the tab is VISIBLE. A backgrounded tab re-reading every polled
    // collections every interval is the dominant Cosmos cost as data grows and users
    // leave tabs open all day. When the tab becomes visible again, refresh once
    // immediately so returning users still see current data. Interval relaxed 30s→60s.
    const POLL_MS = 60000;
    const interval = setInterval(() => { if (!document.hidden) refresh(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated]);

  const overdueCount = proposals.filter((p) => isPreSubmission(p.stage) && dueUrgency(p.dueDate)?.level === "overdue").length;

  // Loading state — MSAL resolving the session, OR authenticated but the DB user profile is
  // still loading (without this second case the login screen would flash back for a beat after
  // a successful sign-in, which reads as "login didn't work"). If the profile load FAILED
  // (userLoadError), fall through to the sign-in screen instead of spinning forever.
  if (authLoading || (isAuthenticated && !user && !userLoadError)) {
    return (
      <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: colors.textDim }}>
          <Spinner size={28} color={colors.accent} />
          <div style={{ fontSize: 14 }}>{isAuthenticated ? "Signing you in…" : "Loading…"}</div>
        </div>
      </div>
    );
  }

  // Not authenticated (or session went stale) — show login
  if (!isAuthenticated || !user) {
    return (
      <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
        <div style={{ width: 420, padding: 48, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, textAlign: "center" }}>
          <img src={LOGO_SVG} alt="Sawtooth Advisors" style={{ height: 48, marginBottom: 12 }} />
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.text, letterSpacing: -0.5, marginBottom: 8 }}>Operations Hub</div>
          {userLoadError ? (
            <div style={{ fontSize: 14, color: colors.danger, marginBottom: 32 }}>Your session expired — please sign in again.</div>
          ) : (
            <div style={{ fontSize: 14, color: colors.textDim, marginBottom: 32 }}>Sign in with your Microsoft account</div>
          )}
          <button
            onClick={() => { setSigningIn(true); setUserLoadError(false); login(); }}
            disabled={signingIn}
            style={{
              width: "100%", padding: 14, background: colors.accent, color: "#fff",
              border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: signingIn ? "default" : "pointer",
              opacity: signingIn ? 0.85 : 1, fontFamily: font, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            {signingIn ? (
              <><Spinner size={18} color="#fff" /> Signing in…</>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 21 21" fill="none"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                Sign in with Microsoft
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    switch (page) {
      case "home": return <HomeScreen onNavigate={navigate} proposals={proposals} archived={archived} events={calEvents} discussions={discussions} />;
      case "proposals": return <ProposalBoard user={user} users={users} />;
      case "calendar": return <CompanyCalendar user={user} users={users} />;
      case "clients": return <ClientsPage user={user} users={users} />;
      case "employees": return <EmployeesPage user={user} users={users} onOpen={(hash, target) => { try { window.history.pushState(null, "", "#" + hash); } catch { /* ignore */ } setPage(target); }} />;
      case "internal": return <InternalPage user={user} users={users} />;
      case "contacts": return <ContactsPage users={users} />;
      // Dashboard links set a deep-link hash then switch page; the target page reads the hash on mount.
      case "dashboard": return <DashboardPage users={users} onOpen={(hash, target) => { try { window.history.pushState(null, "", "#" + hash); } catch { /* ignore */ } setPage(target); }} />;
      case "templates": return <TemplatesPage users={users} />;
      case "meetingtemplates": return user.role === "admin"
        ? <MeetingTemplatesPage users={users} />
        : <div style={{ padding: 40, color: colors.textMuted }}>Meeting templates are managed by admins.</div>;
      case "discussion": return <DiscussionItems user={user} users={users} />;
      case "opsplanner": return <OpsPlanner user={user} users={users} />;
      case "frontdoor": return <FrontDoorPrograms user={user} />;
      case "onepager": return <OnePagerBuilder user={user} />;
      case "pdf-markdown": return (
        <Suspense fallback={<div style={{ color: colors.textMuted, padding: 32 }}>Loading converter…</div>}>
          <PdfToMarkdown />
        </Suspense>
      );
      case "admin": return <AdminPanel users={users} currentUser={user} onUpdateUser={(updated) => setUsers((prev) => prev.map((u) => u.id === updated.id ? updated : u))} onRemoveUser={(id) => setUsers((prev) => prev.filter((u) => u.id !== id))} />;
      case "settings": return <SettingsPage user={user} onUpdateUser={(updated) => {
        setUser(updated);
        setUsers((prev) => prev.map((u) => u.id === updated.id ? updated : u));
      }} />;
      default: return <HomeScreen onNavigate={navigate} proposals={proposals} archived={archived} events={calEvents} discussions={discussions} />;
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: colors.bg, fontFamily: font, color: colors.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <Sidebar active={page} onNavigate={navigate} user={user} onLogout={logout} notifications={overdueCount} />
      <main style={{ flex: 1, padding: "32px 40px", overflowY: "auto", minHeight: "100vh" }}>
        {/* Page Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <div>
            {page !== "home" && (
              <button onClick={() => setPage("home")} style={{
                background: "none", border: "none", color: colors.textDim, fontSize: 12, cursor: "pointer",
                fontFamily: font, padding: 0, marginBottom: 4, display: "block",
              }}>← Home</button>
            )}
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: -0.3 }}>
              {page === "home" ? "" : navItem(page)?.label || (page === "settings" ? "Settings" : page === "admin" ? "Admin" : "")}
            </h1>
          </div>
          {user.role === "admin" && page !== "admin" && page !== "settings" && (
            <button onClick={() => setPage("admin")} style={{
              background: "transparent", border: `1px solid ${colors.borderLight}`, color: colors.textMuted,
              padding: "7px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer", fontFamily: font,
            }}>Team</button>
          )}
        </div>
        {renderPage()}
      </main>
    </div>
  );
}
